/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Stack, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DetailsIcon from '../../../../../../../../../assets/Resources/DetailIcon.svg';
import CategoriesIcon from '../../../../../../../../../assets/Resources/CategoriesIcon.svg';
import AcessIcon from '../../../../../../../../../assets/Resources/AccessIcon.svg';
import { IoPricetagOutline } from 'react-icons/io5';
import { FaRegEdit } from 'react-icons/fa';
import { PiClockCountdownBold } from 'react-icons/pi';
import { LanguageContext } from '../../../../../../../../../libs/Context';
import { DocumentItemWithResponse } from '../types';
import { DocumentStatus } from '../utils/formatters';

// Import components from Reusable folder
import DetailsSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/DetailsSlider';
import CategorySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/CategorySlider';
import TagSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/TagsSlider';
import AccessSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/AccessSlider';
import ManageContentSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ManageContentSlider';
import ActivitySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ActivitySlider';
import useContentPermissions from '../../../Reusable/ContentCards/useContentPermissions';
import { selectedTabs } from '../../../../../constants';

interface DocumentDetailsSliderProps {
  selectedTab?: selectedTabs;
  setSelectedTab: (tab: selectedTabs) => void;
  setOpen: (open: boolean) => void;
  articleCreate?: string;
  documentData?: any;
  selectedItems?: string[];
  documentsList?: DocumentItemWithResponse[];
  companyId?: string;
  onSave?: (updatedData: any, documents: any) => void;
}

// Define a consolidated UI state interface for document data
interface DocumentUIState {
  details: {
    title: string;
    description: string;
    fileSize?: number;
    fileType?: string;
    pageCount?: number;
	url?: string;
    thumbnail?: {
      url: string;
      canEdit?: boolean;
      canDelete?: boolean;
    };
    metadata?: {
      author?: string;
      createdDate?: string;
      modifiedDate?: string;
      pageCount?: number;
      fileType?: string;
    };
  };
  categoryIds: string[];
  tags: string[];
  access: {
    // visibility: any;
    allowDownload: boolean;
    allowShare: boolean;
    // accessControl: {
    //   internalEmployees: {
    //     allSelected: boolean;
    //     departments: Array<{name: string; count?: number}>;
    //     specificUsers: Array<{userId: string; name: string; email: string; role?: string}>;
    //   };
    //   userGroups: Array<{type: string; count?: number}>;
    //   participantCount: number;
    //   participantLimit: number;
    // };
  };
  manage: {
    status: DocumentStatus;
    publishDate?: string;
  };
}

/**
 * DocumentDetailsSlider - A specialized slider for document content details
 */
const DocumentDetailsSlider: React.FC<DocumentDetailsSliderProps> = (props) =>
{
	const {
		selectedTab = 'Details',
		setSelectedTab,
		setOpen,
		articleCreate,
		documentData,
		selectedItems = [],
		documentsList = [],
		companyId,
		onSave
	} = props;

	const { language } = useContext(LanguageContext);
	const { isPrivateShareEnabled, isShareEnabled, isDownloadEnabled } = useContentPermissions();

	// Define the tabs specific to document editing - memoized to prevent unnecessary re-creation
	const documentTabs = useMemo(() =>
	{
		// Base tabs that are always available
		const baseTabs = [
			{ name: 'Categories', Icon: CategoriesIcon, ImageIcon: true },
			{ name: 'Add Tags', Icon: <IoPricetagOutline fontSize={'26px'} />, ImageIcon: false },
			{ name: 'Access', Icon: AcessIcon, ImageIcon: true }
			// { name: 'Activity', Icon: <PiClockCountdownBold fontSize={'26px'} />, ImageIcon: false }
		];

		// Only add the Details tab if a single document is selected
		if (!selectedItems || selectedItems.length <= 1)
		{
			return [
				{ name: 'Details', Icon: DetailsIcon, ImageIcon: true },
				...baseTabs
			];
		}

		return baseTabs;
	}, [ selectedItems ]);

	// Update selectedTab if Details is selected but no longer available
	useEffect(() =>
	{
		if (selectedTab === 'Details' && selectedItems && selectedItems.length > 1)
		{
			// Switch to first available tab if Details is not available
			if (documentTabs.length > 0)
			{
				setSelectedTab(documentTabs[0].name as selectedTabs);
			}
		}
	}, [ selectedItems, selectedTab, documentTabs, setSelectedTab ]);
	console.log(documentData);

	// Simple function to create initial state from document data or empty defaults
	const createInitialState = useCallback((): DocumentUIState =>
	{
		return {
			details : {
				title       : documentData?.title || '',
				description : documentData?.description || '',
				fileSize    : documentData?.fileSize,
				fileType    : documentData?.fileType || undefined,
				pageCount   : documentData?.pageCount,
				url         : documentData?.url || '',
				// thumbnail   : documentData?.coverImage || documentData?.thumbnail
				// 	? {
				// 		url       : documentData.coverImage || documentData.thumbnail?.url,
				// 		canEdit   : documentData?.thumbnail?.canEdit || '',
				// 		canDelete : documentData?.thumbnail?.canDelete || ''
				// 	}
				// 	: undefined,
				metadata    : documentData?.metadata
			},
			categoryIds : documentData?.categoryIds || [],
			tags        : documentData?.tags || [],
			access      : {
				// visibility    : documentData?.visibility !== undefined ? (documentData?.visibility as 'public' | 'private') : isPrivateShareEnabled ? 'private' : 'public',
				allowDownload : documentData?.allowDownload !== undefined ? documentData?.allowDownload : isDownloadEnabled,
				allowShare    : documentData?.allowShare !== undefined ? documentData?.allowShare : isShareEnabled,
				// accessControl : documentData?.accessControl || ''
			},
			manage : {
				status      : documentData?.status || '',
				publishDate : documentData?.publishDate || ''
			}
		};
	}, [ documentData ]);

	// console.log(documentData, '---documentData');

	// Store original state for comparison
	const [ originalData, setOriginalData ] = useState<DocumentUIState | null>(null);

	// Simplified state management - just use the document data or empty defaults
	const [ uiState, setUiState ] = useState<DocumentUIState>(createInitialState);

	// Reset state when selection changes
	useEffect(() =>
	{
		const newState = createInitialState();

		setUiState(newState);
		setOriginalData(newState);
	}, [ documentData, createInitialState ]);

	// Detect changes in state
	const hasChanges = useMemo(() =>
	{
		if (!originalData) return false;

		// Deep comparison of relevant fields
		return (
			originalData.details.title !== uiState.details.title ||
			originalData.details.description !== uiState.details.description ||
			JSON.stringify(originalData.categoryIds) !== JSON.stringify(uiState.categoryIds) ||
			JSON.stringify(originalData.tags) !== JSON.stringify(uiState.tags) ||
			// originalData.access.visibility !== uiState.access.visibility ||
			originalData.access.allowDownload !== uiState.access.allowDownload ||
			originalData.access.allowShare !== uiState.access.allowShare ||
			originalData.manage.status !== uiState.manage.status ||
			originalData.manage.publishDate !== uiState.manage.publishDate
			// JSON.stringify(originalData.access.accessControl) !== JSON.stringify(uiState.access.accessControl)
		);
	}, [ originalData, uiState ]);

	// Generic state updater function that accepts a field path and a value or updater function
	const updateState = useCallback(<K extends keyof DocumentUIState>(
		section: K,
		valueOrUpdater: DocumentUIState[K] | ((prev: DocumentUIState[K]) => DocumentUIState[K])
	) =>
	{
		setUiState((prevState) =>
		{
			const newValue = typeof valueOrUpdater === 'function'
				? (valueOrUpdater as Function)(prevState[section])
				: valueOrUpdater;

			return {
				...prevState,
				[section] : newValue
			};
		});
	}, []);

	// Handle updates to details section (title, description, etc.)
	const handleDetailsUpdate = useCallback((detailsData: any) =>
	{
		updateState('details', (prev) => ({
			...prev,
			...detailsData
		}));
	}, [ updateState ]);

	// Handle updates to categories
	const handleCategoriesUpdate = useCallback((categories: string[]) =>
	{
		updateState('categoryIds', categories);
	}, [ updateState ]);

	// Handle updates to tags
	const handleTagsUpdate = useCallback((tags: string[]) =>
	{
		updateState('tags', tags);
	}, [ updateState ]);

	// Handle updates to access control settings
	const handleAccessUpdate = useCallback((accessData: any) =>
	{
		updateState('access', (prev) => ({
			...prev,
			// visibility    : accessData.visibility || prev.visibility,
			allowDownload : accessData.allowDownload !== undefined ? accessData.allowDownload : prev.allowDownload,
			allowShare    : accessData.allowShare !== undefined ? accessData.allowShare : prev.allowShare,
			// accessControl : accessData.accessControl || prev.accessControl
		}));
	}, [ updateState ]);

	console.log(uiState);

	// Enhanced save function to handle multiple documents
	const handleSave = useCallback(() =>
	{
		// Ensure visibility is either 'public' or 'private'
		const updatedDocData: any = {
			...(uiState.details.title && { title: uiState.details.title }),
			...(uiState.details.description && { description: uiState.details.description }),
			...(uiState.details.url && { url: uiState.details.url }),
			// Only include categoryIds if we're on the Categories tab
			...(uiState.tags.length > 0 && { tags: uiState.tags }),
			// ...(uiState.access.visibility && { visibility: uiState.access.visibility }),
			...(uiState.access.allowDownload !== undefined && { allowDownload: uiState.access.allowDownload }),
			...(uiState.access.allowShare !== undefined && { allowShare: uiState.access.allowShare }),
			...(uiState.manage.status && { status: uiState.manage.status }),
			// ...(uiState.access.accessControl && { accessControl: uiState.access.accessControl })
		};

		if (uiState.categoryIds.length > 0 && uiState.categoryIds[0] !== documentData?.categoryIds[0])
		{
			updatedDocData.categoryIds = uiState.categoryIds;
		}

		// Remove undefined fields to avoid accidentally clearing data

		// Send data back to parent component
		if (onSave)
		{
			if (selectedItems && selectedItems.length > 0)
			{
				// Update all selected documents
				onSave(updatedDocData, selectedItems);
			}
			else if (documentData)
			{
				// Update single document
				onSave(updatedDocData, [ documentData.id ? documentData.id : documentData?._id ]);
			}
		}

		// Close the slider
		setOpen(false);
	}, [ uiState, onSave, setOpen, documentData, selectedItems, selectedTab ]);

	// Memoize the tab content to prevent unnecessary re-renders
	const renderTabContent = useMemo(() =>
	{
		switch (selectedTab)
		{
			case 'Details':
				// Only render Details content if a single document is selected
				if (selectedItems && selectedItems.length > 1)
				{
					return null;
				}

				return (
					<DetailsSlider
						articleCreate={articleCreate}
						data={uiState.details}
						itemType='document'
						onChange={handleDetailsUpdate}
					/>
				);
			case 'Categories':
				return (
					<CategorySlider
						artcileCreate={articleCreate}
						categories={uiState.categoryIds}
						onChange={handleCategoriesUpdate}
					/>
				);
			case 'Add Tags':
				return (
					<TagSlider
						tags={documentData?.tags || []}
						initialSelectedTags={uiState.tags}
						onChange={handleTagsUpdate}
					/>
				);
			case 'Access':
				return (
					<AccessSlider
						visibility={'private'}
						allowDownload={uiState.access.allowDownload}
						allowShare={uiState.access.allowShare}
						accessControl={undefined}
						onChange={handleAccessUpdate}
					/>
				);
			case 'Manage':
				return (
					<ManageContentSlider />
				);
			case 'Activity':
				return (
					<ActivitySlider />
				);
			default:
				return null;
		}
	}, [
		selectedTab,
		selectedItems,
		articleCreate,
		uiState,
		documentData,
		handleDetailsUpdate,
		handleCategoriesUpdate,
		handleTagsUpdate,
		handleAccessUpdate
	]);

	return (
		<Grid container height='100%' width='100%' >
			{/* Left Sidebar Tabs */}
			<Grid item xs={2.5} height={'100%'} sx={{ boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.10)' }}>
				<Stack spacing={2} pt={3}>
					{documentTabs.map((tab, index) => (
						<Stack
							key={index}
							direction='column'
							alignItems='center'
							sx={{
								cursor          : 'pointer',
								padding         : '10px 8px',
								borderRight     : selectedTab === tab.name ? '4px solid #24a4c4' : '4px solid transparent',
								backgroundColor : selectedTab === tab.name ? '#EFFBFF' : ''
							}}
							onClick={() => setSelectedTab(tab.name as selectedTabs)}
						>
							{tab?.ImageIcon
								? (
									<Box width={'28px'} height={'28px'}>
										<img src={`${tab.Icon}`} alt='' className='invertGray' />
									</Box>
								)
								: (tab.Icon)}
							<Typography variant='body2' color={'#737373'} pt={0.2}>{tab.name}</Typography>
						</Stack>
					))}
				</Stack>
			</Grid>

			{/* Right Content Area */}
			<Grid item xs={9.5} height={'100%'} width={'100%'}>
				<Grid container height={'100%'} direction={'column'} display={'flex'}>
					<Grid item px={2} py={1}>
						<Stack direction={'row'} alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
							<Typography fontSize={'22px'} fontWeight={'var(--font500)'}>{selectedTab}</Typography>
							<IconButton onClick={() => setOpen(false)}>
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>
					<Grid item minHeight={0} pt={1} px={2} pb={1} xs sx={{ overflowY: 'auto' }}>
						{renderTabContent}
					</Grid>
					{selectedTab !== 'Activity' && (
						<Grid item>
							<Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'end'} display={'flex'}>
								<Button onClick={() => setOpen(false)} variant='outlined'>
									{language?.cancel || 'Cancel'}
								</Button>
								<Button
									variant='contained'
									sx={{ color: 'var(--whiteColor)' }}
									onClick={handleSave}
									disabled={!hasChanges}
								>
									{language?.save || 'Save'}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default DocumentDetailsSlider;